import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Sync a tab id with `?tab=` in the URL so sidebar deep-links work.
 */
export function useTabSearchParam<T extends string>(
  allowed: readonly T[],
  fallback: T,
): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams();

  const tab = useMemo(() => {
    const raw = params.get('tab');
    if (raw && (allowed as readonly string[]).includes(raw)) return raw as T;
    return fallback;
  }, [params, allowed, fallback]);

  const setTab = useCallback(
    (next: T) => {
      setParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set('tab', next);
          return nextParams;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return [tab, setTab];
}

export default useTabSearchParam;
